import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import {
  createTextNode,
  findSceneNode,
  getAllSceneNodes,
  getSceneNodeChildren,
  resolveActivePageId,
  type CurvaDocument,
} from "@/document/model/document";
import {
  createDefaultFrameFlexLayout,
  frameCornerRadii,
  frameFlexLayout,
  frameMirrorLayout,
  frameRepeatLayout,
  nodeConstraints,
  type AutoLayoutMode,
  type BooleanOperation,
  type ComponentInstanceNode,
  type ComponentNode,
  type FlexAxisSizing,
  type FlexLayoutAlign,
  type FlexLayoutDirection,
  type FlexLayoutJustify,
  type MirrorLayoutAxis,
  type NodeConstraints,
  type RepeatLayoutMode,
  type SceneNode,
} from "@/document/model/nodes";
import {
  createDefaultRichText,
  textWithUniformStylePatch,
  type TextLayoutMode,
} from "@/document/model/text";
import {
  createBackgroundBlurEffect,
  createDropShadowEffect,
  createGradientStop,
  createInnerShadowEffect,
  createLayerBlurEffect,
  createLinearGradientPaint,
  createRadialGradientPaint,
  createSolidPaint,
  normalizeGradientPaint,
  styleWithStrokeStack,
  type GradientColorSpace,
  type GradientPoint,
  type GradientStop,
  type NodeEffect,
  type NodeStyle,
  type Paint,
  type StrokeAlign,
} from "@/document/model/styles";
import { parseColor, oklchToHex } from "@/document/model/color";
import type { PrimitiveKind } from "@/document/model/primitives";
import {
  createStyleFromSelection,
  documentStyleUsageCount,
  findDocumentStyle,
  getEffectiveTextModel,
  listDocumentStylesByKind,
  normalizeStyleLibrary,
  styleBindingId,
  type DocumentStyle,
  type DocumentStyleKind,
} from "@/document/model/documentStyles";
import { filterFontCatalogFamilies } from "@/lib/fontCatalog";
import { loadGoogleFontWeights } from "@/lib/GoogleFontLoader";
import {
  mergeTypography,
  readOptionalTypographyFields,
  summarizeTypography,
} from "@/mcp/typographyParams";
import {
  InsertNodeCommand,
  MoveNodesInTreeCommand,
  RenameNodeCommand,
  type NodeTreeDropTarget,
  type ReorderPlacement,
} from "@/commands/nodeCommands";
import { TransactionCommand } from "@/commands/transaction";
import { exportSceneNodeToPngBlob } from "@/export/pngExporter";
import { getSelectionBounds } from "@/editor/tools/hitTest";
import { cloneNodeForPaste } from "@/editor/shell/shellTransfer";
import { importSvgMarkup } from "@/import/svg/svgImporter";
import type { AlignmentAction } from "@/editor/shell/right-panel/alignment";
import {
  alignSelectedNodes,
  applyBooleanOperation,
  addComponentVariant,
  createComponent,
  detachInstance,
  drawFrame,
  drawPrimitive,
  flattenSelectedNodes,
  frameSelectedNodes,
  groupSelectedNodes,
  insertComponentInstance,
  outlineStrokeForSelection,
  placeNodeInParent,
  reorderNodes,
  ungroupSelectedNodes,
  updateInstanceVariant,
  type StructureHandlerOptions,
} from "@/editor/commands/handlers/structureHandlers";
import { componentVariants } from "@/document/operations/componentSystem";
import {
  deleteNodes,
  duplicateSelectedObjects,
} from "@/editor/commands/handlers/clipboardHandlers";
import {
  updateNodeBounds,
  updateNodeTransforms,
} from "@/editor/commands/handlers/nodeHandlers";
import {
  selectNodes,
} from "@/editor/commands/handlers/selectionHandlers";
import {
  applyDocumentStyle,
  createDocumentStyle,
  createDocumentScriptEntry,
  createScriptFolderEntry,
  deleteDocumentStyle,
  deleteScriptEntry,
  detachDocumentStyle,
  duplicateDocumentScript,
  moveScriptEntry,
  renameScriptEntry,
  setFrameScriptId,
  updateDocumentScriptSource,
  updateDocumentStyle,
  updateFrameCornerRadii,
  updateFrameLayout,
  updateNodeConstraints,
  updatePrimitiveDefinition,
  updateSelectedNodeStyles,
} from "@/editor/commands/handlers/styleHandlers";
import {
  createEmptyScriptLibrary,
  findDocumentScript,
  findScriptEntry,
  findScriptFolder,
  framesAttachedToScript,
  listDocumentScripts,
  type DocumentScript,
  type ScriptEntry,
  type ScriptLibrary,
} from "@/document/model/documentScripts";
import {
  parseScriptParams,
  type ScriptParam,
  type ScriptParamValue,
} from "@/scripting/scriptParams";
import {
  updateTextCharacters,
  updateTextNode,
} from "@/editor/commands/handlers/textHandlers";
import { mcpToolDefinition } from "@/mcp/tools";
import { parseBatchToolCalls, runBatchedTools } from "@/mcp/batchTools";
import { getMcpDocs } from "@/mcp/agentDocs";
import {
  documentBoundsFromInspectorBounds,
  inspectorBoundsFromDocumentBounds,
  parentFrameContextForNode,
  type BoundsFrameContext,
} from "@/editor/shell/right-panel/boundsFrameContext";
import type { Rect } from "@/lib/math/rect";

export interface McpToolSession {
  commandHost: EditorCommandHost;
  /**
   * Run a document script without the pre-run params modal.
   * Merges `values` over `@param` defaults. Registered by EditorShell.
   */
  runDocumentScript?: (
    scriptId: string,
    options?: McpRunScriptOptions,
  ) => Promise<McpRunScriptResult>;
}

export interface McpRunScriptOptions {
  /** Frame that becomes the Luau `frame` global. Defaults to selection context. */
  contextFrameId?: string;
  /** `@param` values for this run only (never written back to script source). */
  values?: Record<string, string | number | boolean>;
}

export interface McpRunScriptResult {
  ok: boolean;
  scriptId: string;
  scriptName: string;
  contextFrameId?: string;
  logs: string[];
  error?: string;
  applied: boolean;
  realtime: boolean;
  message: string;
}

let session: McpToolSession | undefined;

export function setMcpToolSession(next: McpToolSession | undefined): void {
  session = next;
}

export function getMcpToolSession(): McpToolSession | undefined {
  return session;
}

export async function dispatchMcpTool(method: string, params: unknown): Promise<unknown> {
  if (!mcpToolDefinition(method)) {
    throw new Error(`Unknown tool "${method}".`);
  }

  const p = readParams(params);

  switch (method) {
    case "undo":
      return runUndo();
    case "redo":
      return runRedo();
    case "get_document_json":
      return document();
    case "batch_tools":
      return batchTools(p);
    case "get_docs":
      return getMcpDocs(optionalString(p, "topic"));
    case "get_document_summary":
      return getDocumentSummary();
    case "list_pages":
      return listPages();
    case "get_selection":
      return getSelection();
    case "select_nodes":
      return runSelectNodes(p);
    case "get_node":
      return getNode(requireString(p, "nodeId"));
    case "list_children":
      return listChildren(requireString(p, "parentId"));
    case "find_nodes":
      return findNodes(p);
    case "create_rectangle":
      return createPrimitive("rectangle", p);
    case "create_oval":
      return createPrimitive("oval", p);
    case "create_line":
      return createPrimitive("line", p);
    case "create_frame":
      return createFrameTool(p);
    case "create_text":
      return createTextTool(p);
    case "import_svg":
      return importSvgTool(p);
    case "set_fill":
      return setFill(p);
    case "set_gradient_fill":
      return setGradientFill(p);
    case "set_effects":
      return setEffects(p);
    case "set_stroke":
      return setStroke(p);
    case "set_opacity":
      return setOpacity(p);
    case "set_corner_radius":
      return setCornerRadius(p);
    case "set_text":
      return setText(p);
    case "set_typography":
      return setTypography(p);
    case "set_text_layout":
      return setTextLayout(p);
    case "list_fonts":
      return listFonts(p);
    case "list_styles":
      return listStylesTool(p);
    case "get_style":
      return getStyleTool(p);
    case "create_style":
      return createStyleTool(p);
    case "apply_style":
      return applyStyleTool(p);
    case "detach_style":
      return detachStyleTool(p);
    case "update_style":
      return updateStyleTool(p);
    case "delete_style":
      return deleteStyleTool(p);
    case "set_bounds":
      return setBounds(p);
    case "set_rotation":
      return setRotation(p);
    case "rename_node":
      return renameNode(p);
    case "delete_nodes":
      return deleteNodesTool(p);
    case "reparent_nodes":
      return reparentNodes(p);
    case "group_nodes":
      return groupNodesTool(p);
    case "frame_nodes":
      return frameNodesTool(p);
    case "ungroup_nodes":
      return ungroupNodesTool(p);
    case "reorder_nodes":
      return reorderNodesTool(p);
    case "align_nodes":
      return alignNodesTool(p);
    case "set_auto_layout":
      return setAutoLayout(p);
    case "set_constraints":
      return setConstraintsTool(p);
    case "boolean_operation":
      return booleanOperationTool(p);
    case "flatten_nodes":
      return flattenNodesTool(p);
    case "outline_stroke":
      return outlineStrokeTool(p);
    case "list_components":
      return listComponentsTool(p);
    case "create_component":
      return createComponentTool(p);
    case "add_component_variant":
      return addComponentVariantTool(p);
    case "insert_instance":
      return insertInstanceTool(p);
    case "set_instance_variant":
      return setInstanceVariantTool(p);
    case "duplicate_nodes":
      return duplicateNodesTool(p);
    case "detach_instance":
      return detachInstanceTool(p);
    case "export_node_png":
      return exportNodePng(p);
    case "export_selection_png":
      return exportSelectionPng(p);
    case "list_scripts":
      return listScriptsTool();
    case "get_script":
      return getScriptTool(p);
    case "create_script":
      return createScriptTool(p);
    case "update_script":
      return updateScriptTool(p);
    case "delete_script":
      return deleteScriptTool(p);
    case "duplicate_script":
      return duplicateScriptTool(p);
    case "create_script_folder":
      return createScriptFolderTool(p);
    case "move_script":
      return moveScriptTool(p);
    case "attach_script":
      return attachScriptTool(p);
    case "detach_script":
      return detachScriptTool(p);
    case "run_script":
      return runScriptTool(p);
    default:
      throw new Error(`Tool "${method}" is not implemented yet.`);
  }
}

function requireHost(): EditorCommandHost {
  if (!session?.commandHost) {
    throw new Error("Editor command host is not attached to the MCP bridge.");
  }
  return session.commandHost;
}

function document(): CurvaDocument {
  return documentStore.getDocument();
}

function structureOptions(): StructureHandlerOptions {
  return {
    getActivePageId: () => resolveActivePageId(document(), selectionStore.getSelection().activePageId),
  };
}

function readParams(params: unknown): Record<string, unknown> {
  if (params == null) {
    return {};
  }
  if (typeof params !== "object" || Array.isArray(params)) {
    throw new Error("Tool params must be an object.");
  }
  return params as Record<string, unknown>;
}

function requireString(params: Record<string, unknown>, key: string): string {
  const value = params[key];
  if (typeof value !== "string" || !value) {
    throw new Error(`Missing string param "${key}".`);
  }
  return value;
}

function requireNumber(params: Record<string, unknown>, key: string): number {
  const value = params[key];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Missing number param "${key}".`);
  }
  return value;
}

function optionalString(params: Record<string, unknown>, key: string): string | undefined {
  const value = params[key];
  return typeof value === "string" && value ? value : undefined;
}

function optionalNumber(params: Record<string, unknown>, key: string): number | undefined {
  const value = params[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function requireStringArray(params: Record<string, unknown>, key: string): string[] {
  const value = params[key];
  if (!Array.isArray(value) || value.some(item => typeof item !== "string")) {
    throw new Error(`Missing string[] param "${key}".`);
  }
  return value as string[];
}

function optionalStringArray(params: Record<string, unknown>, key: string): string[] | undefined {
  const value = params[key];
  if (value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value) || value.some(item => typeof item !== "string")) {
    throw new Error(`Param "${key}" must be string[].`);
  }
  return value as string[];
}

function readBounds(params: Record<string, unknown>): Rect {
  return {
    x: requireNumber(params, "x"),
    y: requireNumber(params, "y"),
    width: requireNumber(params, "width"),
    height: requireNumber(params, "height"),
  };
}

// MCP coordinates are expressed relative to the containing frame, mirroring the
// editor's right-panel inspector (nearest ancestor frame; groups and the page
// don't offset). `node.bounds` are stored in document/world space, so convert on
// the way in (relative -> document) and out (document -> relative).
function frameContextForParent(parentId: string | undefined): BoundsFrameContext | undefined {
  if (!parentId) {
    return undefined;
  }
  const doc = document();
  const parent = findSceneNode(doc, parentId);
  if (parent?.type === "frame") {
    return { frameId: parent.id, frameBounds: parent.bounds };
  }
  // Group / boolean / instance parent, or a page: fall back to the nearest
  // ancestor frame of the parent (matching how the inspector picks its origin).
  return parentFrameContextForNode(doc, parentId);
}

function documentBoundsForParent(relativeBounds: Rect, parentId: string | undefined): Rect {
  return documentBoundsFromInspectorBounds(relativeBounds, frameContextForParent(parentId));
}

function documentPointForParent(x: number, y: number, parentId: string | undefined): { x: number; y: number } {
  const world = documentBoundsForParent({ x, y, width: 0, height: 0 }, parentId);
  return { x: world.x, y: world.y };
}

function inspectorBoundsForNode(node: SceneNode): Rect {
  return inspectorBoundsFromDocumentBounds(node.bounds, parentFrameContextForNode(document(), node.id));
}

function selectedOrParamIds(params: Record<string, unknown>): string[] {
  const fromParams = optionalStringArray(params, "nodeIds");
  if (fromParams && fromParams.length > 0) {
    return fromParams;
  }
  const selected = selectionStore.getSelection().selectedNodeIds;
  if (selected.length === 0) {
    throw new Error("Provide nodeIds or select nodes in the editor.");
  }
  return selected;
}

function ensureSelection(nodeIds: string[], primaryNodeId?: string): void {
  selectNodes(requireHost(), nodeIds, primaryNodeId ?? nodeIds[0], { recordHistory: false });
}

function primaryCreatedId(): string {
  const id = selectionStore.getSelection().primarySelectedNodeId ?? selectionStore.getSelection().selectedNodeIds[0];
  if (!id) {
    throw new Error("Create operation did not select a node.");
  }
  return id;
}

async function batchTools(params: Record<string, unknown>) {
  const calls = parseBatchToolCalls(params.calls);
  return runBatchedTools(calls, (name, toolParams) => dispatchMcpTool(name, toolParams));
}

function runUndo(): { ok: true; undone: boolean } {
  const host = requireHost();
  if (host.history.getState(document().revision).undoDepth === 0) {
    return { ok: true, undone: false };
  }
  host.undo();
  return { ok: true, undone: true };
}

function runRedo(): { ok: true; redone: boolean } {
  const host = requireHost();
  if (host.history.getState(document().revision).redoDepth === 0) {
    return { ok: true, redone: false };
  }
  host.redo();
  return { ok: true, redone: true };
}

function listPages() {
  return document().pages.map(page => ({
    id: page.id,
    name: page.name,
    childCount: page.children.length,
  }));
}

function getSelection() {
  const doc = document();
  const selection = selectionStore.getSelection();
  return {
    activePageId: resolveActivePageId(doc, selection.activePageId),
    selectedNodeIds: selection.selectedNodeIds,
    primarySelectedNodeId: selection.primarySelectedNodeId,
    mode: selection.mode,
  };
}

function runSelectNodes(params: Record<string, unknown>) {
  const nodeIds = requireStringArray(params, "nodeIds");
  const primary = optionalString(params, "primaryNodeId") ?? nodeIds[0];
  ensureSelection(nodeIds, primary);
  return getSelection();
}

function getNode(nodeId: string) {
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  return summarizeNode(node, { includeChildrenCount: true });
}

function listChildren(parentId: string) {
  const doc = document();
  const page = doc.pages.find(candidate => candidate.id === parentId);
  if (page) {
    return {
      parentId,
      parentType: "page",
      children: page.children.map(child => summarizeNode(child)),
    };
  }

  const node = findSceneNode(doc, parentId);
  if (!node) {
    throw new Error(`Parent "${parentId}" was not found.`);
  }
  const children = getSceneNodeChildren(node);
  if (!children) {
    throw new Error(`Node "${parentId}" (${node.type}) has no children.`);
  }
  return {
    parentId,
    parentType: node.type,
    children: children.map(child => summarizeNode(child)),
  };
}

function findNodes(params: Record<string, unknown>) {
  const nameNeedle = optionalString(params, "name")?.trim().toLowerCase();
  const typeFilter = optionalString(params, "type");
  const limitRaw = optionalNumber(params, "limit");
  const limit = limitRaw && limitRaw > 0 ? Math.min(200, Math.floor(limitRaw)) : 50;

  const matches = getAllSceneNodes(document()).filter(node => {
    if (typeFilter && node.type !== typeFilter) {
      return false;
    }
    if (nameNeedle && !node.name.toLowerCase().includes(nameNeedle)) {
      return false;
    }
    return true;
  });

  return {
    count: matches.length,
    truncated: matches.length > limit,
    nodes: matches.slice(0, limit).map(node => summarizeNode(node)),
  };
}

function getDocumentSummary() {
  const doc = document();
  const selection = getSelection();
  const nodes = getAllSceneNodes(doc);
  const countsByType: Record<string, number> = {};
  for (const node of nodes) {
    countsByType[node.type] = (countsByType[node.type] ?? 0) + 1;
  }

  const activePage = doc.pages.find(page => page.id === selection.activePageId) ?? doc.pages[0];
  const topLevel = (activePage?.children ?? []).map(node => summarizeNode(node, { includeChildrenCount: true }));

  return {
    id: doc.id,
    name: doc.name,
    revision: doc.revision,
    pageCount: doc.pages.length,
    nodeCount: nodes.length,
    styleCount: normalizeStyleLibrary(doc.styles).items.length,
    styles: normalizeStyleLibrary(doc.styles).items.map(summarizeStyle),
    scriptCount: listDocumentScripts(doc.scripts).length,
    scripts: listDocumentScripts(doc.scripts).map(script => ({
      id: script.id,
      name: script.name,
      attachedFrameCount: framesAttachedToScript(doc, script.id).length,
      paramCount: parseScriptParams(script.source).length,
    })),
    countsByType,
    activePageId: selection.activePageId,
    selection,
    topLevel,
  };
}

function createPrimitive(kind: PrimitiveKind, params: Record<string, unknown>) {
  const host = requireHost();
  const parentId = optionalString(params, "parentId");
  const resolvedParentId = parentId ?? structureOptions().getActivePageId();
  const bounds = documentBoundsForParent(readBounds(params), resolvedParentId);
  drawPrimitive(host, kind, bounds, parentId, structureOptions());
  const nodeId = primaryCreatedId();
  const fill = optionalString(params, "fill");
  if (fill && kind !== "line") {
    setFill({ nodeIds: [nodeId], color: fill });
  }
  const name = optionalString(params, "name");
  if (name) {
    renameNode({ nodeId, name });
  }
  return getNode(nodeId);
}

function createFrameTool(params: Record<string, unknown>) {
  const host = requireHost();
  const parentId = optionalString(params, "parentId");
  const resolvedParentId = parentId ?? structureOptions().getActivePageId();
  const bounds = documentBoundsForParent(readBounds(params), resolvedParentId);
  drawFrame(host, bounds, parentId, optionalString(params, "name"), structureOptions());
  return getNode(primaryCreatedId());
}

function createTextTool(params: Record<string, unknown>) {
  const host = requireHost();
  const characters = requireString(params, "characters");
  const parentId = optionalString(params, "parentId") ?? structureOptions().getActivePageId();
  if (!parentId) {
    throw new Error("No parent page/frame available.");
  }

  const { x, y } = documentPointForParent(requireNumber(params, "x"), requireNumber(params, "y"), parentId);

  const width = optionalNumber(params, "width");
  const height = optionalNumber(params, "height");
  const context = host.commandContext();
  const parent = findSceneNode(context.document, parentId);
  const page = context.document.pages.find(candidate => candidate.id === parentId);
  const siblings = parent ? getSceneNodeChildren(parent) : page?.children;
  const count = siblings?.filter(node => node.type === "text").length ?? 0;
  const node = createTextNode(optionalString(params, "name") ?? `Text ${count + 1}`, x, y, characters);
  const typographyPatch = readOptionalTypographyFields(params);
  const rich = createDefaultRichText(characters);
  if (Object.keys(typographyPatch).length > 0) {
    rich.style = { ...rich.style, ...typographyPatch };
  }
  node.text = {
    ...rich,
    textLayout: width !== undefined && height !== undefined ? "FIXED_SIZE" : "AUTO_WIDTH",
  };
  if (width !== undefined && height !== undefined) {
    node.bounds = { x, y, width, height };
  } else {
    const size = node.text.style.fontSize;
    const estimatedWidth = Math.max(40, characters.length * size * 0.55);
    node.bounds = { x, y, width: estimatedWidth, height: size * 1.25 };
  }
  const placed = placeNodeInParent(parentId, node, node.bounds);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [placed.id],
    primarySelectedNodeId: placed.id,
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(new InsertNodeCommand(parentId, placed), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, `Inserted ${placed.name}`);

  if (typographyPatch.fontFamily) {
    void loadGoogleFontWeights(typographyPatch.fontFamily).catch(error => {
      console.warn("Failed to load Google font for create_text", error);
    });
  }

  const fill = optionalString(params, "fill");
  if (fill) {
    setFill({ nodeIds: [placed.id], color: fill });
  }
  return getNode(placed.id);
}

function importSvgTool(params: Record<string, unknown>) {
  const host = requireHost();
  const svg = requireString(params, "svg");
  if (svg.length > 1_000_000) {
    throw new Error("SVG markup must be 1 MB or smaller.");
  }

  const parentId = optionalString(params, "parentId") ?? structureOptions().getActivePageId();
  if (!parentId) {
    throw new Error("No parent page/frame available.");
  }

  const { x, y } = documentPointForParent(requireNumber(params, "x"), requireNumber(params, "y"), parentId);
  const context = host.commandContext();
  const parentNode = findSceneNode(context.document, parentId);
  const parentPage = context.document.pages.find(page => page.id === parentId);
  if (!parentPage && (!parentNode || !getSceneNodeChildren(parentNode))) {
    throw new Error(`Parent "${parentId}" is not a page or container node.`);
  }

  const imported = importSvgMarkup(svg, { name: optionalString(params, "name") });
  if (imported.nodes.length === 0) {
    throw new Error("SVG did not contain importable content.");
  }

  const offset = {
    x: x - imported.bounds.x,
    y: y - imported.bounds.y,
  };
  const placedNodes = imported.nodes.map(sourceNode => {
    const cloned = cloneNodeForPaste(sourceNode, context.document, offset);
    return placeNodeInParent(parentId, cloned, cloned.bounds);
  });
  const commands = placedNodes.map(node => new InsertNodeCommand(parentId, node));
  const nodeIds = placedNodes.map(node => node.id);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: nodeIds,
    primarySelectedNodeId: nodeIds.at(-1),
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });

  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Import SVG", commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, nodeIds.length > 1 ? `Imported ${nodeIds.length} SVG objects` : "Imported SVG");

  return {
    ok: true,
    nodeIds,
    nodes: nodeIds.map(getNode),
    issues: imported.issues,
  };
}

function setFill(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const color = requireString(params, "color");
  detachBoundStyles(host, nodeIds, "fill");
  const paint = createSolidPaint(color);
  const updates = nodeIds.flatMap(nodeId => {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      return [];
    }
    const style: NodeStyle = { ...node.style, fills: [paint] };
    return [{ nodeId, style }];
  });
  updateSelectedNodeStyles(host, updates, "Set fill");
  return { ok: true, nodeIds: updates.map(update => update.nodeId) };
}

function setGradientFill(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const type = requireString(params, "type");
  const stops = readGradientStops(params.stops);
  const colorSpace = optionalString(params, "colorSpace") ?? "rgb";
  if (colorSpace !== "rgb" && colorSpace !== "oklch") {
    throw new Error("colorSpace must be rgb|oklch.");
  }

  const paint = type === "linear"
    ? normalizeGradientPaint({
        ...createLinearGradientPaint(),
        angle: optionalNumber(params, "angle") ?? 0,
        start: readOptionalGradientPoint(params.start, "start"),
        end: readOptionalGradientPoint(params.end, "end"),
        colorSpace: colorSpace as GradientColorSpace,
        stops,
      })
    : type === "radial"
      ? normalizeGradientPaint({
          ...createRadialGradientPaint(),
          center: readOptionalGradientPoint(params.center, "center") ?? { x: 0.5, y: 0.5 },
          radiusPoint: readOptionalGradientPoint(params.radiusPoint, "radiusPoint"),
          radius: optionalNumber(params, "radius") ?? 0.5,
          colorSpace: colorSpace as GradientColorSpace,
          stops,
        })
      : undefined;
  if (!paint) {
    throw new Error("type must be linear|radial.");
  }

  detachBoundStyles(host, nodeIds, "fill");
  const updates = nodeIds.flatMap(nodeId => {
    const node = findSceneNode(document(), nodeId);
    return node ? [{ nodeId, style: { ...node.style, fills: [structuredClone(paint)] } }] : [];
  });
  updateSelectedNodeStyles(host, updates, "Set gradient fill");
  return { ok: true, nodeIds: updates.map(update => update.nodeId), paint };
}

function setEffects(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  if (!Array.isArray(params.effects)) {
    throw new Error('Missing array param "effects".');
  }
  const effects = params.effects.map((value, index) => readEffect(value, index));
  detachBoundStyles(host, nodeIds, "effects");
  const updates = nodeIds.flatMap(nodeId => {
    const node = findSceneNode(document(), nodeId);
    return node ? [{ nodeId, style: { ...node.style, effects: structuredClone(effects) } }] : [];
  });
  updateSelectedNodeStyles(host, updates, "Set effects");
  return { ok: true, nodeIds: updates.map(update => update.nodeId), effects };
}

function readGradientStops(value: unknown): GradientStop[] {
  if (!Array.isArray(value) || value.length < 2) {
    throw new Error("stops must contain at least two gradient stops.");
  }
  return value.map((entry, index) => {
    const stop = requireRecord(entry, `stops[${index}]`);
    return createGradientStop(requireNumber(stop, "position"), requireString(stop, "color"));
  });
}

function readOptionalGradientPoint(value: unknown, label: string): GradientPoint | undefined {
  if (value === undefined) {
    return undefined;
  }
  const point = requireRecord(value, label);
  return { x: requireNumber(point, "x"), y: requireNumber(point, "y") };
}

function readEffect(value: unknown, index: number): NodeEffect {
  const params = requireRecord(value, `effects[${index}]`);
  const type = requireString(params, "type");
  const visible = optionalBoolean(params, "visible") ?? true;
  if (type === "dropShadow" || type === "innerShadow") {
    const base = type === "dropShadow" ? createDropShadowEffect() : createInnerShadowEffect();
    return {
      ...base,
      visible,
      offsetX: optionalNumber(params, "offsetX") ?? base.offsetX,
      offsetY: optionalNumber(params, "offsetY") ?? base.offsetY,
      blur: Math.max(0, optionalNumber(params, "blur") ?? base.blur),
      spread: optionalNumber(params, "spread") ?? base.spread,
      color: parseColor(requireString(params, "color")),
    };
  }
  if (type === "layerBlur") {
    const base = createLayerBlurEffect();
    const mode = optionalString(params, "mode") ?? base.mode;
    if (mode !== "uniform" && mode !== "progressive") {
      throw new Error(`effects[${index}].mode must be uniform|progressive.`);
    }
    const blur = Math.max(0, requireNumber(params, "blur"));
    return {
      ...base,
      visible,
      mode,
      blur,
      start: Math.max(0, optionalNumber(params, "start") ?? blur),
      end: Math.max(0, optionalNumber(params, "end") ?? blur),
    };
  }
  if (type === "backgroundBlur") {
    return {
      ...createBackgroundBlurEffect(),
      visible,
      blur: Math.max(0, requireNumber(params, "blur")),
    };
  }
  throw new Error(`effects[${index}].type must be dropShadow|innerShadow|layerBlur|backgroundBlur.`);
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function optionalBoolean(params: Record<string, unknown>, key: string): boolean | undefined {
  const value = params[key];
  return typeof value === "boolean" ? value : undefined;
}

function parseStrokeAlign(value: string | undefined): StrokeAlign {
  if (value === undefined) {
    return "inside";
  }
  if (value !== "inside" && value !== "center" && value !== "outside") {
    throw new Error('align must be "inside", "center", or "outside".');
  }
  return value;
}

function setStroke(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const color = requireString(params, "color");
  const width = optionalNumber(params, "width") ?? 1;
  const align = parseStrokeAlign(optionalString(params, "align"));
  detachBoundStyles(host, nodeIds, "stroke");
  const updates = nodeIds.flatMap(nodeId => {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      return [];
    }
    const style = styleWithStrokeStack(node.style, [{ paint: createSolidPaint(color), width, align }]);
    return [{ nodeId, style }];
  });
  updateSelectedNodeStyles(host, updates, "Set stroke");
  return { ok: true, nodeIds: updates.map(update => update.nodeId) };
}

function setOpacity(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const opacity = Math.min(1, Math.max(0, requireNumber(params, "opacity")));
  const updates = nodeIds.flatMap(nodeId => {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      return [];
    }
    return [{ nodeId, style: { ...node.style, opacity } }];
  });
  updateSelectedNodeStyles(host, updates, "Set opacity");
  return { ok: true, nodeIds: updates.map(update => update.nodeId) };
}

function setCornerRadius(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const radius = optionalNumber(params, "radius");
  const radiiParam = params.radii;
  let radii: [number, number, number, number];
  if (Array.isArray(radiiParam) && radiiParam.length === 4 && radiiParam.every(item => typeof item === "number")) {
    radii = radiiParam as [number, number, number, number];
  } else if (radius !== undefined) {
    radii = [radius, radius, radius, radius];
  } else {
    throw new Error("Provide radius or radii[4].");
  }

  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      continue;
    }
    if (node.type === "frame") {
      updateFrameCornerRadii(host, nodeId, radii);
    } else if (node.type === "primitive" && node.primitive.kind === "rectangle") {
      updatePrimitiveDefinition(host, nodeId, { ...node.primitive, cornerRadii: radii });
    }
  }
  return { ok: true, nodeIds, radii };
}

function setText(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const characters = requireString(params, "characters");
  updateTextCharacters(host, nodeId, characters);
  return getNode(nodeId);
}

function setTypography(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  if (nodeIds.length === 0) {
    throw new Error('Param "nodeIds" must contain at least one id.');
  }
  const patch = readOptionalTypographyFields(params);
  if (Object.keys(patch).length === 0) {
    throw new Error("Provide at least one typography field (fontFamily, fontWeight, fontSize, letterSpacing, horizontalAlign, verticalAlign, or lineHeight).");
  }

  detachBoundStyles(host, nodeIds, "text");

  if (patch.fontFamily) {
    void loadGoogleFontWeights(patch.fontFamily).catch(error => {
      console.warn("Failed to load Google font for set_typography", error);
    });
  }

  const updated: string[] = [];
  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      throw new Error(`Node "${nodeId}" was not found.`);
    }
    if (node.type !== "text") {
      throw new Error(`Node "${nodeId}" is not a text node.`);
    }
    updateTextNode(host, nodeId, textWithUniformStylePatch(node.text, patch), "Set typography");
    updated.push(nodeId);
  }

  return { ok: true, nodeIds: updated, typography: patch };
}

function setTextLayout(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  if (nodeIds.length === 0) {
    throw new Error('Param "nodeIds" must contain at least one id.');
  }
  const textLayout = requireString(params, "textLayout");
  if (textLayout !== "AUTO_WIDTH" && textLayout !== "AUTO_HEIGHT" && textLayout !== "FIXED_SIZE") {
    throw new Error('textLayout must be AUTO_WIDTH|AUTO_HEIGHT|FIXED_SIZE.');
  }
  const layout = textLayout as TextLayoutMode;

  const updated: string[] = [];
  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      throw new Error(`Node "${nodeId}" was not found.`);
    }
    if (node.type !== "text") {
      throw new Error(`Node "${nodeId}" is not a text node.`);
    }
    if (node.text.textLayout !== layout) {
      updateTextNode(host, nodeId, { ...node.text, textLayout: layout }, "Set text layout");
    }
    updated.push(nodeId);
  }

  return { ok: true, nodeIds: updated, textLayout: layout };
}

function listFonts(params: Record<string, unknown>) {
  const query = optionalString(params, "query") ?? "";
  const limitRaw = optionalNumber(params, "limit");
  const limit = limitRaw === undefined ? 40 : Math.min(200, Math.max(1, Math.floor(limitRaw)));
  const matched = filterFontCatalogFamilies(query);
  return {
    count: matched.length,
    families: matched.slice(0, limit),
    truncated: matched.length > limit,
  };
}

function setBounds(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const context = parentFrameContextForNode(document(), nodeId);
  const bounds = documentBoundsFromInspectorBounds(readBounds(params), context);
  updateNodeBounds(host, [{ nodeId, bounds }], "Updated bounds");
  return getNode(nodeId);
}

function setRotation(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const rotation = requireNumber(params, "rotation");
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  updateNodeTransforms(host, [{
    nodeId,
    transform: { ...node.transform, rotation },
  }], "Updated rotation");
  return getNode(nodeId);
}

function renameNode(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const name = requireString(params, "name");
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  host.runDocumentCommands(
    [new RenameNodeCommand(nodeId, node.name, name, node.nameManuallyEdited === true, true)],
    "Renamed node",
  );
  return getNode(nodeId);
}

function deleteNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  deleteNodes(host, nodeIds);
  return { ok: true, deleted: nodeIds };
}

function reparentNodes(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const parentId = requireString(params, "parentId");
  const indexParam = optionalNumber(params, "index");
  const doc = document();
  const page = doc.pages.find(candidate => candidate.id === parentId);
  const parentNode = findSceneNode(doc, parentId);
  let parentType: NodeTreeDropTarget["parentType"] | undefined;
  let siblingCount = 0;
  if (page) {
    parentType = "page";
    siblingCount = page.children.length;
  } else if (
    parentNode?.type === "frame"
    || parentNode?.type === "group"
    || parentNode?.type === "component"
    || parentNode?.type === "componentVariant"
    || parentNode?.type === "instance"
    || parentNode?.type === "boolean"
  ) {
    parentType = parentNode.type;
    siblingCount = getSceneNodeChildren(parentNode)?.length ?? 0;
  }
  if (!parentType) {
    throw new Error(`Parent "${parentId}" is not a valid container.`);
  }
  const index = indexParam === undefined ? siblingCount : Math.max(0, Math.floor(indexParam));
  const target: NodeTreeDropTarget = { parentId, parentType, index };
  host.runDocumentCommands([new MoveNodesInTreeCommand(nodeIds, target)], "Reparented nodes");
  return { ok: true, nodeIds, parentId, index };
}

function groupNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  ensureSelection(selectedOrParamIds(params));
  groupSelectedNodes(host);
  return getSelection();
}

function frameNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  ensureSelection(selectedOrParamIds(params));
  frameSelectedNodes(host);
  return getSelection();
}

function ungroupNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  ensureSelection(selectedOrParamIds(params));
  ungroupSelectedNodes(host);
  return getSelection();
}

function reorderNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  const placement = requireString(params, "placement") as ReorderPlacement;
  if (!["front", "back", "forward", "backward"].includes(placement)) {
    throw new Error("placement must be front|back|forward|backward.");
  }
  const nodeIds = selectedOrParamIds(params);
  reorderNodes(host, nodeIds, placement);
  return { ok: true, nodeIds, placement };
}

function alignNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  const action = requireString(params, "action") as AlignmentAction;
  const nodeIds = selectedOrParamIds(params);
  ensureSelection(nodeIds);
  const bounds = getSelectionBounds(document(), nodeIds);
  alignSelectedNodes(host, action, bounds);
  return { ok: true, nodeIds, action };
}

function setAutoLayout(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const mode = requireString(params, "mode") as AutoLayoutMode;
  if (mode !== "off" && mode !== "flex" && mode !== "repeat" && mode !== "mirror") {
    throw new Error("mode must be off|flex|repeat|mirror.");
  }
  const node = findSceneNode(document(), nodeId);
  if (node?.type !== "frame") {
    throw new Error(`Node "${nodeId}" is not a frame.`);
  }
  const currentFlex = frameFlexLayout(node) ?? createDefaultFrameFlexLayout();
  const currentRepeat = frameRepeatLayout(node);
  const currentMirror = frameMirrorLayout(node);
  const direction = optionalString(params, "direction") as FlexLayoutDirection | undefined;
  const gap = optionalNumber(params, "gap");
  const padding = optionalNumber(params, "padding");
  const justifyContent = optionalString(params, "justifyContent") as FlexLayoutJustify | undefined;
  const alignItems = optionalString(params, "alignItems") as Exclude<FlexLayoutAlign, "auto"> | undefined;
  const widthSizing = optionalString(params, "widthSizing") as FlexAxisSizing | undefined;
  const heightSizing = optionalString(params, "heightSizing") as FlexAxisSizing | undefined;
  const repeatMode = optionalString(params, "repeatMode") as RepeatLayoutMode | undefined;
  const count = optionalNumber(params, "count");
  const columns = optionalNumber(params, "columns");
  const rows = optionalNumber(params, "rows");
  const columnGap = optionalNumber(params, "columnGap");
  const rowGap = optionalNumber(params, "rowGap");
  const radius = optionalNumber(params, "radius");
  const startAngle = optionalNumber(params, "startAngle");
  const mirrorAxis = optionalString(params, "mirrorAxis") as MirrorLayoutAxis | undefined;
  updateFrameLayout(host, nodeId, {
    autoLayout: mode,
    flexLayout: {
      ...currentFlex,
      ...(direction ? { direction } : {}),
      ...(gap !== undefined ? { gap } : {}),
      ...(padding !== undefined ? { padding: { top: padding, right: padding, bottom: padding, left: padding } } : {}),
      ...(justifyContent ? { justifyContent } : {}),
      ...(alignItems ? { alignItems } : {}),
      ...(widthSizing ? { widthSizing } : {}),
      ...(heightSizing ? { heightSizing } : {}),
    },
    repeatLayout: {
      ...currentRepeat,
      ...(repeatMode ? { mode: repeatMode } : {}),
      ...(count !== undefined ? { count } : {}),
      ...(columns !== undefined ? { columns } : {}),
      ...(rows !== undefined ? { rows } : {}),
      ...(gap !== undefined ? { gap } : {}),
      ...(columnGap !== undefined ? { columnGap } : {}),
      ...(rowGap !== undefined ? { rowGap } : {}),
      ...(radius !== undefined ? { radius } : {}),
      ...(startAngle !== undefined ? { startAngle } : {}),
    },
    mirrorLayout: {
      ...currentMirror,
      ...(mirrorAxis ? { axis: mirrorAxis } : {}),
      ...(gap !== undefined ? { gap } : {}),
    },
  });
  return getNode(nodeId);
}

function setConstraintsTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  const current = nodeConstraints(node);
  const next: NodeConstraints = {
    ...current,
    ...(optionalString(params, "horizontal") ? { horizontal: optionalString(params, "horizontal") as NodeConstraints["horizontal"] } : {}),
    ...(optionalString(params, "vertical") ? { vertical: optionalString(params, "vertical") as NodeConstraints["vertical"] } : {}),
  };
  updateNodeConstraints(host, nodeId, next);
  return getNode(nodeId);
}

async function booleanOperationTool(params: Record<string, unknown>) {
  const host = requireHost();
  const operation = requireString(params, "operation") as BooleanOperation;
  if (!["union", "subtract", "intersect", "exclude"].includes(operation)) {
    throw new Error("operation must be union|subtract|intersect|exclude.");
  }
  ensureSelection(selectedOrParamIds(params));
  await applyBooleanOperation(host, operation);
  return getSelection();
}

async function flattenNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  ensureSelection(selectedOrParamIds(params));
  await flattenSelectedNodes(host);
  return getSelection();
}

async function outlineStrokeTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = selectedOrParamIds(params);
  ensureSelection(nodeIds);
  await outlineStrokeForSelection(host, nodeIds);
  return getSelection();
}

function listComponentsTool(params: Record<string, unknown>) {
  const nameNeedle = optionalString(params, "name")?.trim().toLowerCase();
  const nodes = getAllSceneNodes(document());
  const instances = nodes.filter((node): node is ComponentInstanceNode => node.type === "instance");
  const components = nodes
    .filter((node): node is ComponentNode => node.type === "component")
    .filter(component => !nameNeedle || component.name.toLowerCase().includes(nameNeedle))
    .map(component => {
      const variants = componentVariants(component).map(variant => ({
        id: variant.id,
        name: variant.name,
        bounds: variant.bounds,
      }));
      const matchingInstances = instances.filter(instance => instance.componentId === component.id);
      const instancesByVariant = Object.fromEntries(
        variants.map(variant => [
          variant.id,
          matchingInstances.filter(instance => instance.variantId === variant.id).length,
        ]),
      );
      return {
        id: component.id,
        name: component.name,
        bounds: component.bounds,
        variantCount: variants.length,
        variants,
        instanceCount: matchingInstances.length,
        instancesByVariant,
      };
    });

  return {
    count: components.length,
    components,
  };
}

function createComponentTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = selectedOrParamIds(params);
  createComponent(host, nodeIds, structureOptions());
  return getSelection();
}

function addComponentVariantTool(params: Record<string, unknown>) {
  const host = requireHost();
  const componentId = requireString(params, "componentId");
  const component = findSceneNode(document(), componentId);
  if (component?.type !== "component") {
    throw new Error(`Component "${componentId}" was not found.`);
  }
  const beforeIds = new Set(component.children.map(variant => variant.id));
  addComponentVariant(host, componentId, structureOptions());
  const updated = findSceneNode(document(), componentId);
  if (updated?.type !== "component") {
    throw new Error(`Component "${componentId}" disappeared after adding a variant.`);
  }
  const variant = updated.children.find(child => !beforeIds.has(child.id)) ?? updated.children.at(-1);
  if (!variant) {
    throw new Error(`Component "${componentId}" has no variants after add.`);
  }
  return {
    ok: true,
    componentId,
    variantId: variant.id,
    variant: getNode(variant.id),
    variants: updated.children.map(child => summarizeNode(child)),
  };
}

function insertInstanceTool(params: Record<string, unknown>) {
  const host = requireHost();
  const componentId = requireString(params, "componentId");
  const variantId = optionalString(params, "variantId");
  const parentId = optionalString(params, "parentId");
  const component = findSceneNode(document(), componentId);
  if (component?.type !== "component") {
    throw new Error(`Component "${componentId}" was not found.`);
  }
  const selectedVariant = variantId
    ? componentVariants(component).find(variant => variant.id === variantId)
    : componentVariants(component)[0];
  if (!selectedVariant) {
    throw new Error(variantId
      ? `Variant "${variantId}" does not belong to component "${componentId}".`
      : `Component "${componentId}" has no variants.`);
  }

  // With parentId: x,y are frame-relative top-left (like create_*).
  // Without: x,y stay document-space and choose the drop frame (instance is centered on the point).
  let dropPoint = { x: requireNumber(params, "x"), y: requireNumber(params, "y") };
  if (parentId) {
    const worldTopLeft = documentPointForParent(dropPoint.x, dropPoint.y, parentId);
    dropPoint = {
      x: worldTopLeft.x + selectedVariant.bounds.width / 2,
      y: worldTopLeft.y + selectedVariant.bounds.height / 2,
    };
  }

  const instanceId = insertComponentInstance(
    host,
    componentId,
    dropPoint,
    structureOptions(),
    selectedVariant.id,
    parentId,
  );
  return getNode(instanceId);
}

function setInstanceVariantTool(params: Record<string, unknown>) {
  const host = requireHost();
  const instanceId = requireString(params, "instanceId");
  const variantId = requireString(params, "variantId");
  const instance = findSceneNode(document(), instanceId);
  if (instance?.type !== "instance") {
    throw new Error(`Node "${instanceId}" is not an instance.`);
  }
  updateInstanceVariant(host, instanceId, variantId, structureOptions());
  return getNode(instanceId);
}

function duplicateNodesTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = selectedOrParamIds(params);
  const offsetX = optionalNumber(params, "offsetX");
  const offsetY = optionalNumber(params, "offsetY");
  const offset = {
    x: offsetX ?? 20,
    y: offsetY ?? 20,
  };
  duplicateSelectedObjects(host, offset, nodeIds);
  const duplicatedIds = selectionStore.getSelection().selectedNodeIds;
  if (duplicatedIds.length === 0) {
    throw new Error("Nothing was duplicated.");
  }
  return {
    ok: true,
    nodeIds: duplicatedIds,
    nodes: duplicatedIds.map(nodeId => getNode(nodeId)),
  };
}

function detachInstanceTool(params: Record<string, unknown>) {
  const host = requireHost();
  detachInstance(host, requireString(params, "instanceId"), structureOptions());
  return getSelection();
}

async function exportNodePng(params: Record<string, unknown>) {
  const nodeId = requireString(params, "nodeId");
  const scale = optionalNumber(params, "scale") ?? 1;
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  const blob = await exportSceneNodeToPngBlob(document(), node, scale);
  const dataUrl = await blobToDataUrl(blob);
  return { nodeId, scale, mimeType: "image/png", dataUrl };
}

async function exportSelectionPng(params: Record<string, unknown>) {
  const nodeId = optionalString(params, "nodeId")
    ?? selectionStore.getSelection().primarySelectedNodeId
    ?? selectionStore.getSelection().selectedNodeIds[0];
  if (!nodeId) {
    throw new Error("No selection and no nodeId provided.");
  }
  return exportNodePng({ ...params, nodeId });
}

function requireScriptsEnabled(): void {
  if (!uiStore.allowScripts.getValue()) {
    throw new Error("Scripts are disabled. Enable Allow Scripts in the editor (View menu).");
  }
}

function scriptLibrary(): ScriptLibrary {
  return document().scripts ?? createEmptyScriptLibrary();
}

function summarizeScriptParam(param: ScriptParam) {
  return {
    name: param.name,
    label: param.label,
    widget: param.widget,
    min: param.min,
    max: param.max,
    step: param.step,
    options: param.options,
    defaultValue: param.defaultValue,
  };
}

function summarizeScript(script: DocumentScript, options: { includeSource?: boolean } = {}) {
  const params = parseScriptParams(script.source);
  const attachedFrames = framesAttachedToScript(document(), script.id).map(frame => ({
    id: frame.id,
    name: frame.name,
  }));
  return {
    id: script.id,
    name: script.name,
    parentFolderId: parentFolderIdForEntry(scriptLibrary(), script.id),
    attachedFrames,
    params: params.map(summarizeScriptParam),
    ...(options.includeSource ? { source: script.source } : { sourceLength: script.source.length }),
  };
}

function parentFolderIdForEntry(library: ScriptLibrary, entryId: string): string | undefined {
  const search = (
    entries: ScriptEntry[],
    folderId: string | undefined,
  ): { found: true; folderId: string | undefined } | undefined => {
    for (const entry of entries) {
      if (entry.id === entryId) {
        return { found: true, folderId };
      }
      if (entry.type === "folder") {
        const nested = search(entry.children, entry.id);
        if (nested) {
          return nested;
        }
      }
    }
    return undefined;
  };
  return search(library.items, undefined)?.folderId;
}

function listScriptsTool() {
  requireScriptsEnabled();
  const library = scriptLibrary();
  return {
    scripts: listDocumentScripts(library).map(script => summarizeScript(script)),
    folders: listScriptFoldersSummary(library.items),
  };
}

function listScriptFoldersSummary(
  entries: ScriptEntry[],
  parentFolderId?: string,
): Array<{ id: string; name: string; parentFolderId?: string; childCount: number }> {
  const out: Array<{ id: string; name: string; parentFolderId?: string; childCount: number }> = [];
  for (const entry of entries) {
    if (entry.type !== "folder") {
      continue;
    }
    out.push({
      id: entry.id,
      name: entry.name,
      parentFolderId,
      childCount: entry.children.length,
    });
    out.push(...listScriptFoldersSummary(entry.children, entry.id));
  }
  return out;
}

function getScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const scriptId = requireString(params, "scriptId");
  const script = findDocumentScript(scriptLibrary(), scriptId);
  if (!script) {
    throw new Error(`Script "${scriptId}" was not found.`);
  }
  return summarizeScript(script, { includeSource: true });
}

function createScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const host = requireHost();
  const name = optionalString(params, "name");
  const parentFolderId = optionalString(params, "parentFolderId");
  if (parentFolderId && !findScriptFolder(scriptLibrary(), parentFolderId)) {
    throw new Error(`Script folder "${parentFolderId}" was not found.`);
  }
  const scriptId = createDocumentScriptEntry(host, name, parentFolderId);
  const source = optionalString(params, "source");
  if (source !== undefined) {
    updateDocumentScriptSource(host, scriptId, source);
  }
  const script = findDocumentScript(scriptLibrary(), scriptId);
  if (!script) {
    throw new Error("Created script was not found.");
  }
  return summarizeScript(script, { includeSource: true });
}

function updateScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const host = requireHost();
  const scriptId = requireString(params, "scriptId");
  const script = findDocumentScript(scriptLibrary(), scriptId);
  if (!script) {
    throw new Error(`Script "${scriptId}" was not found.`);
  }
  const name = optionalString(params, "name");
  const source = optionalString(params, "source");
  if (name === undefined && source === undefined) {
    throw new Error("Provide name and/or source to update.");
  }
  if (name !== undefined) {
    renameScriptEntry(host, scriptId, name);
  }
  if (source !== undefined) {
    updateDocumentScriptSource(host, scriptId, source);
  }
  const updated = findDocumentScript(scriptLibrary(), scriptId);
  if (!updated) {
    throw new Error(`Script "${scriptId}" was not found after update.`);
  }
  return summarizeScript(updated, { includeSource: true });
}

function deleteScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const entryId = requireString(params, "scriptId");
  const entry = findScriptEntry(scriptLibrary(), entryId);
  if (!entry) {
    throw new Error(`Script entry "${entryId}" was not found.`);
  }
  deleteScriptEntry(requireHost(), entryId);
  return { ok: true as const, deletedId: entryId, type: entry.type };
}

function duplicateScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const scriptId = requireString(params, "scriptId");
  if (!findDocumentScript(scriptLibrary(), scriptId)) {
    throw new Error(`Script "${scriptId}" was not found.`);
  }
  const createdId = duplicateDocumentScript(requireHost(), scriptId);
  if (!createdId) {
    throw new Error("Failed to duplicate script.");
  }
  const script = findDocumentScript(scriptLibrary(), createdId);
  if (!script) {
    throw new Error("Duplicated script was not found.");
  }
  return summarizeScript(script, { includeSource: true });
}

function createScriptFolderTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const folderId = createScriptFolderEntry(requireHost(), optionalString(params, "name"));
  const folder = findScriptFolder(scriptLibrary(), folderId);
  if (!folder) {
    throw new Error("Created script folder was not found.");
  }
  return {
    id: folder.id,
    name: folder.name,
    type: "folder" as const,
    childCount: folder.children.length,
  };
}

function moveScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const entryId = requireString(params, "scriptId");
  if (!findScriptEntry(scriptLibrary(), entryId)) {
    throw new Error(`Script entry "${entryId}" was not found.`);
  }
  const parentFolderId = optionalString(params, "parentFolderId");
  if (parentFolderId && !findScriptFolder(scriptLibrary(), parentFolderId)) {
    throw new Error(`Script folder "${parentFolderId}" was not found.`);
  }
  const index = optionalNumber(params, "index");
  const toIndex = index === undefined ? 0 : Math.max(0, Math.floor(index));
  moveScriptEntry(requireHost(), entryId, parentFolderId, toIndex);
  const entry = findScriptEntry(scriptLibrary(), entryId);
  return {
    ok: true as const,
    scriptId: entryId,
    type: entry?.type,
    parentFolderId: parentFolderIdForEntry(scriptLibrary(), entryId),
    index: toIndex,
  };
}

function attachScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const frameId = requireString(params, "frameId");
  const scriptId = requireString(params, "scriptId");
  const node = findSceneNode(document(), frameId);
  if (node?.type !== "frame") {
    throw new Error(`Frame "${frameId}" was not found.`);
  }
  if (!findDocumentScript(scriptLibrary(), scriptId)) {
    throw new Error(`Script "${scriptId}" was not found.`);
  }
  setFrameScriptId(requireHost(), frameId, scriptId);
  return getNode(frameId);
}

function detachScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const frameId = requireString(params, "frameId");
  const node = findSceneNode(document(), frameId);
  if (node?.type !== "frame") {
    throw new Error(`Frame "${frameId}" was not found.`);
  }
  setFrameScriptId(requireHost(), frameId, undefined);
  return getNode(frameId);
}

async function runScriptTool(params: Record<string, unknown>) {
  requireScriptsEnabled();
  const scriptId = requireString(params, "scriptId");
  if (!findDocumentScript(scriptLibrary(), scriptId)) {
    throw new Error(`Script "${scriptId}" was not found.`);
  }
  const runner = session?.runDocumentScript;
  if (!runner) {
    throw new Error("Script runner is not attached to the MCP bridge. Reconnect the editor session.");
  }
  const contextFrameId = optionalString(params, "contextFrameId");
  if (contextFrameId) {
    const node = findSceneNode(document(), contextFrameId);
    if (node?.type !== "frame") {
      throw new Error(`Context frame "${contextFrameId}" was not found.`);
    }
  }
  const values = readScriptParamValues(params);
  return runner(scriptId, { contextFrameId, values });
}

function readScriptParamValues(
  params: Record<string, unknown>,
): Record<string, ScriptParamValue> | undefined {
  if (!("values" in params) || params.values === undefined) {
    return undefined;
  }
  const raw = params.values;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("values must be an object of param name → number | string | boolean.");
  }
  const out: Record<string, ScriptParamValue> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
      out[key] = value;
      continue;
    }
    throw new Error(`values.${key} must be a number, string, or boolean.`);
  }
  return out;
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return `data:image/png;base64,${btoa(binary)}`;
}

function summarizeNode(node: SceneNode, options: { includeChildrenCount?: boolean } = {}) {
  const children = getSceneNodeChildren(node);
  const summary: Record<string, unknown> = {
    id: node.id,
    name: node.name,
    type: node.type,
    visible: node.visible,
    locked: node.locked,
    bounds: inspectorBoundsForNode(node),
    rotation: node.transform.rotation ?? 0,
  };

  if (node.type === "primitive") {
    summary.primitiveKind = node.primitive.kind;
  }
  if (node.type === "text") {
    summary.characters = node.text.characters.slice(0, 120);
    const effective = getEffectiveTextModel(document(), node) ?? node.text;
    summary.textLayout = effective.textLayout;
    summary.typography = summarizeTypography(effective.style);
  }
  if (node.type === "frame") {
    summary.autoLayout = node.autoLayout;
    summary.clipsContent = node.clipsContent;
    summary.cornerRadii = frameCornerRadii(node);
    if (node.scriptId) {
      summary.scriptId = node.scriptId;
      const script = findDocumentScript(document().scripts, node.scriptId);
      if (script) {
        summary.scriptName = script.name;
      }
    }
  }
  if (node.styleBindings && Object.keys(node.styleBindings).length > 0) {
    summary.styleBindings = { ...node.styleBindings };
  }
  if (options.includeChildrenCount) {
    summary.childCount = children?.length ?? 0;
  }

  return summary;
}

function summarizeStyle(style: DocumentStyle) {
  const usageCount = documentStyleUsageCount(getAllSceneNodes(document()), style.id);
  const base = {
    id: style.id,
    name: style.name,
    kind: style.kind,
    usageCount,
  };

  switch (style.kind) {
    case "fill":
      return {
        ...base,
        fills: style.fills.map(summarizePaint),
      };
    case "stroke":
      return {
        ...base,
        strokes: style.strokes.map(stroke => ({
          width: stroke.width,
          align: stroke.align ?? "inside",
          paint: summarizePaint(stroke.paint),
        })),
      };
    case "text":
      return {
        ...base,
        typography: style.typography,
      };
    case "effects":
      return {
        ...base,
        effects: style.effects,
      };
  }
}

function summarizePaint(paint: Paint) {
  if (paint.type === "solid") {
    return { type: "solid", color: oklchToHex(paint.color) };
  }
  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    return {
      type: paint.type,
      stops: paint.stops.map(stop => ({
        position: stop.position,
        color: oklchToHex(stop.color),
      })),
    };
  }
  return { type: paint.type };
}

function parseStyleKind(value: string | undefined, label = "kind"): DocumentStyleKind {
  if (value !== "fill" && value !== "stroke" && value !== "text" && value !== "effects") {
    throw new Error(`${label} must be fill|stroke|text|effects.`);
  }
  return value;
}

function detachBoundStyles(host: EditorCommandHost, nodeIds: string[], kind: DocumentStyleKind): void {
  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (node && styleBindingId(node, kind)) {
      detachDocumentStyle(host, nodeId, kind);
    }
  }
}

function listStylesTool(params: Record<string, unknown>) {
  const kind = optionalString(params, "kind");
  const styles = kind
    ? listDocumentStylesByKind(document(), parseStyleKind(kind))
    : normalizeStyleLibrary(document().styles).items;
  return {
    count: styles.length,
    styles: styles.map(summarizeStyle),
  };
}

function getStyleTool(params: Record<string, unknown>) {
  const styleId = requireString(params, "styleId");
  const style = findDocumentStyle(document(), styleId);
  if (!style) {
    throw new Error(`Style "${styleId}" was not found.`);
  }
  return summarizeStyle(style);
}

function createStyleTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeId = requireString(params, "nodeId");
  const kind = parseStyleKind(requireString(params, "kind"));
  const name = requireString(params, "name");
  const node = findSceneNode(document(), nodeId);
  if (!node) {
    throw new Error(`Node "${nodeId}" was not found.`);
  }
  if (kind === "text" && node.type !== "text") {
    throw new Error("Text styles can only be created from text nodes.");
  }

  const styleId = createDocumentStyle(host, nodeId, kind, name);
  if (!styleId) {
    throw new Error(`Failed to create ${kind} style.`);
  }
  const style = findDocumentStyle(document(), styleId);
  if (!style) {
    throw new Error(`Created style "${styleId}" was not found after create.`);
  }
  return {
    ok: true,
    styleId,
    style: summarizeStyle(style),
    node: getNode(nodeId),
  };
}

function applyStyleTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const styleId = requireString(params, "styleId");
  const style = findDocumentStyle(document(), styleId);
  if (!style) {
    throw new Error(`Style "${styleId}" was not found.`);
  }

  const applied: string[] = [];
  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      continue;
    }
    if (style.kind === "text" && node.type !== "text") {
      throw new Error(`Cannot apply text style to non-text node "${nodeId}".`);
    }
    applyDocumentStyle(host, nodeId, styleId);
    applied.push(nodeId);
  }
  return {
    ok: true,
    styleId,
    kind: style.kind,
    nodeIds: applied,
    nodes: applied.map(getNode),
  };
}

function detachStyleTool(params: Record<string, unknown>) {
  const host = requireHost();
  const nodeIds = requireStringArray(params, "nodeIds");
  const kind = parseStyleKind(requireString(params, "kind"));
  const detached: string[] = [];
  for (const nodeId of nodeIds) {
    const node = findSceneNode(document(), nodeId);
    if (!node) {
      continue;
    }
    if (!styleBindingId(node, kind)) {
      continue;
    }
    detachDocumentStyle(host, nodeId, kind);
    detached.push(nodeId);
  }
  return {
    ok: true,
    kind,
    nodeIds: detached,
    nodes: detached.map(getNode),
  };
}

function updateStyleTool(params: Record<string, unknown>) {
  const host = requireHost();
  const styleId = requireString(params, "styleId");
  const existing = findDocumentStyle(document(), styleId);
  if (!existing) {
    throw new Error(`Style "${styleId}" was not found.`);
  }

  let next: DocumentStyle = structuredClone(existing);
  const name = optionalString(params, "name");
  if (name) {
    next = { ...next, name: name.trim() || next.name };
  }

  const fromNodeId = optionalString(params, "fromNodeId");
  if (fromNodeId) {
    const source = findSceneNode(document(), fromNodeId);
    if (!source) {
      throw new Error(`Node "${fromNodeId}" was not found.`);
    }
    const snapshot = createStyleFromSelection(source, existing.kind, next.name);
    next = { ...snapshot, id: existing.id, name: next.name };
  }

  const color = optionalString(params, "color");
  const width = optionalNumber(params, "width");
  const align = optionalString(params, "align");
  const effectsParam = params.effects;
  const typographyParam = params.typography;

  if (color !== undefined || width !== undefined || align !== undefined || effectsParam !== undefined || typographyParam !== undefined) {
    next = patchStyleContent(next, {
      color,
      width,
      align: align ? parseStrokeAlign(align) : undefined,
      effects: effectsParam,
      typography: typographyParam,
    });
  }

  updateDocumentStyle(host, next);
  const updated = findDocumentStyle(document(), styleId);
  if (!updated) {
    throw new Error(`Style "${styleId}" disappeared after update.`);
  }
  return {
    ok: true,
    style: summarizeStyle(updated),
  };
}

function patchStyleContent(
  style: DocumentStyle,
  patch: {
    color?: string;
    width?: number;
    align?: StrokeAlign;
    effects?: unknown;
    typography?: unknown;
  },
): DocumentStyle {
  switch (style.kind) {
    case "fill": {
      if (patch.color === undefined) {
        if (patch.width !== undefined || patch.align !== undefined || patch.effects !== undefined || patch.typography !== undefined) {
          throw new Error("Fill styles only accept color (or fromNodeId / name).");
        }
        return style;
      }
      return {
        ...style,
        fills: [createSolidPaint(patch.color)],
      };
    }
    case "stroke": {
      if (patch.effects !== undefined || patch.typography !== undefined) {
        throw new Error("Stroke styles only accept color / width / align (or fromNodeId / name).");
      }
      const current = style.strokes[0] ?? { paint: createSolidPaint("#000000"), width: 1, align: "inside" as const };
      return {
        ...style,
        strokes: [{
          paint: patch.color ? createSolidPaint(patch.color) : structuredClone(current.paint),
          width: patch.width ?? current.width,
          align: patch.align ?? current.align ?? "inside",
        }],
      };
    }
    case "effects": {
      if (patch.color !== undefined || patch.width !== undefined || patch.align !== undefined || patch.typography !== undefined) {
        throw new Error("Effects styles only accept effects (or fromNodeId / name).");
      }
      if (patch.effects === undefined) {
        return style;
      }
      if (!Array.isArray(patch.effects)) {
        throw new Error("effects must be an array.");
      }
      return {
        ...style,
        effects: patch.effects.map((value, index) => readEffect(value, index)),
      };
    }
    case "text": {
      if (patch.color !== undefined || patch.width !== undefined || patch.align !== undefined || patch.effects !== undefined) {
        throw new Error("Text styles only accept typography (or fromNodeId / name).");
      }
      if (patch.typography === undefined) {
        return style;
      }
      return {
        ...style,
        typography: mergeTypography(style.typography, patch.typography),
      };
    }
  }
}

function deleteStyleTool(params: Record<string, unknown>) {
  const host = requireHost();
  const styleId = requireString(params, "styleId");
  const style = findDocumentStyle(document(), styleId);
  if (!style) {
    throw new Error(`Style "${styleId}" was not found.`);
  }
  const usageCount = documentStyleUsageCount(getAllSceneNodes(document()), styleId);
  deleteDocumentStyle(host, styleId);
  return {
    ok: true,
    styleId,
    kind: style.kind,
    detachedNodeCount: usageCount,
  };
}
